import * as React from 'react';
import { AlertTriangle, GitBranch, Plus, Send, Zap } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Callout,
  Quote,
  RecordRow,
  WorkspaceButton,
  linkishClass,
} from '@/components/ui/workspace';
import type { PainPoint, QuotedEvidence } from '@/lib/api/discovery-findings';
import {
  useMapProcessesQuery,
  useProcessMapQuery,
  type MapProcess,
  type ProcessMapDto,
} from '@/lib/api/process-maps';
import { cx } from '@/lib/utils/cn';
import { plural } from '../discovery-logic';
import { DetailLabel } from './area-panes';
import { EvidenceCite, SaidRow, nameBesideCitation } from './evidence-bits';
import { EVIDENCE_LABEL_TEXT, SEVERITY_LABEL, SEVERITY_TONE } from './findings-logic';
import {
  laneRuns,
  stepCoverage,
  stepRows,
  undescribedLine,
  type StepQuote,
  type StepRow,
} from './process-steps';

/**
 * The Process view of an area (the Findings area page, 5 Nov 2026): the
 * process as a list you can read. Each step that does work is a row, in
 * order, grouped by who does it; a described step shows the quote that
 * describes it in the speaker's own words, who said it and where; a step
 * with no quote says so and where it came from. The detail on the right
 * shows everything the selected step rests on.
 *
 * Nothing here is new data: the rows are the current map version's own
 * steps, lanes, citations and pain points, read through the maps API, so
 * the list and the map cannot disagree. Map content is still changed in
 * one place only, the Process maps tab.
 */

export interface AreaProcess {
  /** The area's processes, as the maps tab lists them. */
  processes: MapProcess[];
  process: MapProcess | null;
  select: (processId: string) => void;
  /** The current map version; null while the process has none. */
  map: ProcessMapDto | null;
  rows: StepRow[];
  coverage: { total: number; described: number };
  loading: boolean;
  error: unknown;
}

/** The process of an area that is showing, its map and its steps. */
export function useAreaProcess(
  engagementId: string,
  processIds: readonly string[],
  /** Read the map again every few seconds: AI is drafting its step questions. */
  pollMap = false,
): AreaProcess {
  const wanted = processIds.join(',');
  const list = useMapProcessesQuery(engagementId, processIds.length > 0);
  const [picked, setPicked] = React.useState<string | null>(null);
  const processes = React.useMemo(() => {
    const ids = wanted ? wanted.split(',') : [];
    return ids.flatMap((id) => (list.data ?? []).filter((p) => p.process_id === id));
  }, [list.data, wanted]);
  const process = processes.find((p) => p.process_id === picked) ?? processes[0] ?? null;
  const mapId = process?.map?.id ?? null;
  const map = useProcessMapQuery(engagementId, mapId ?? '', null, mapId !== null, pollMap);
  const rows = React.useMemo(() => (map.data ? stepRows(map.data) : []), [map.data]);
  const coverage = React.useMemo(() => stepCoverage(rows), [rows]);
  return {
    processes,
    process,
    select: setPicked,
    map: mapId !== null ? (map.data ?? null) : null,
    rows,
    coverage,
    loading: processIds.length > 0 && (list.isLoading || (mapId !== null && map.isLoading)),
    error: list.error ?? (mapId !== null ? map.error : null),
  };
}

/** The whole process at a glance: one mark per step, green when described. */
export function StepsBar({ rows }: { rows: readonly StepRow[] }) {
  return (
    <span aria-hidden="true" className="inline-flex items-center gap-[2px]">
      {rows.map((r) => (
        <span
          key={r.key}
          className={cx(
            'block h-[9px] w-[5px] rounded-[2px]',
            r.described ? 'bg-posture-healthy' : 'bg-ink-200',
          )}
        />
      ))}
    </span>
  );
}

function stepState(row: StepRow): { tone: 'healthy' | 'at-risk' | 'unset'; label: string } {
  if (row.described) {
    return {
      tone: 'healthy',
      label: row.quotes.length
        ? `Described · ${plural(row.quotes.length, 'quote')}`
        : 'Validated by the client',
    };
  }
  if (row.needs_attention) return { tone: 'at-risk', label: 'Needs evidence' };
  return { tone: 'unset', label: 'Not described yet' };
}

function PainChip({ pain, number }: { pain: PainPoint | undefined; number: string | null }) {
  return (
    <StatusChip
      tone={pain?.severity ? SEVERITY_TONE[pain.severity] : 'neutral'}
      size="sm"
      title={pain?.text}
    >
      {number ?? 'Pain point'}
      {pain?.severity ? ` · ${SEVERITY_LABEL[pain.severity]}` : ''}
    </StatusChip>
  );
}

function StepItem({
  row,
  selected,
  quote,
  evidence,
  pains,
  onSelect,
  onAsk,
}: {
  row: StepRow;
  selected: boolean;
  /** The first quote the step cites, if any. */
  quote: StepQuote | undefined;
  /** That quote as the area knows it: who said it and where. Absent for another area's. */
  evidence: QuotedEvidence | undefined;
  pains: ReadonlyMap<string, PainPoint>;
  onSelect: () => void;
  onAsk?: () => void;
}) {
  const who = evidence ? nameBesideCitation(evidence.citation, evidence.source) : null;
  const more = row.quotes.length - 1;
  return (
    <div
      className={cx(
        'flex items-start gap-2 border-l-[3px] border-t border-t-ink-100 pr-4',
        selected ? 'border-l-ink-900 bg-paper-50' : 'border-l-transparent',
        !selected && row.described && 'bg-paper-0',
      )}
    >
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        className="grid min-w-0 flex-1 grid-cols-[22px_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 py-2 pl-[13px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600"
      >
        <span
          className={cx(
            'row-span-3 mt-px inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] font-mono text-[10.5px]',
            row.described
              ? 'border-posture-healthy bg-paper-0 text-ink-900'
              : 'border-dashed border-ink-300 text-ink-500',
          )}
        >
          {row.no}
        </span>
        <span
          className={cx(
            'text-body-sm leading-[22px]',
            row.described ? 'font-semibold text-ink-900' : 'font-medium text-ink-700',
          )}
        >
          {row.name}
        </span>
        {quote ? (
          <>
            <span className="col-start-2 line-clamp-2 border-l-2 border-ink-200 pl-2.5 font-serif text-[13.5px] leading-snug text-ink-900">
              “{quote.text}”
            </span>
            <span className="col-start-2 flex flex-wrap items-center gap-1.5 text-meta text-ink-500">
              {who ? <span className="font-medium text-ink-700">{who}</span> : null}
              {evidence ? (
                <EvidenceCite citation={evidence.citation} />
              ) : (
                <span>from another area</span>
              )}
              {quote.number ? (
                <span className="font-mono text-meta font-medium text-ink-700">{quote.number}</span>
              ) : null}
              {evidence?.label ? (
                <span className="rounded-chip bg-paper-100 px-1.5 text-[11px] text-ink-700">
                  {EVIDENCE_LABEL_TEXT[evidence.label]}
                </span>
              ) : null}
              {more > 0 ? <span>+{more} more</span> : null}
            </span>
          </>
        ) : (
          <span className="col-start-2 text-meta text-ink-500">
            {row.described ? 'The client validated this step.' : undescribedLine(row)}
          </span>
        )}
      </button>
      <div className="flex flex-none items-center gap-1.5 py-2.5">
        {row.pain_points.map((p) => (
          <PainChip key={p.id} pain={pains.get(p.id)} number={p.number} />
        ))}
        {!row.described && onAsk ? (
          <button type="button" className={linkishClass()} onClick={onAsk}>
            Ask about it
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** A process that has no map version yet: what stands in the way, and where to draft it. */
function NotDrafted({ process, onOpenMap }: { process: MapProcess; onOpenMap: () => void }) {
  const open = <WorkspaceButton onClick={onOpenMap}>Open Process maps</WorkspaceButton>;
  if (process.draft?.status === 'drafting') {
    return (
      <Callout tone="ai" icon={Zap} role="status">
        AI is drafting the map of {process.name}. Its steps appear here when it is ready.
      </Callout>
    );
  }
  if (process.draft?.status === 'failed') {
    return (
      <Callout tone="warn" icon={AlertTriangle} actions={open}>
        The draft of {process.name} did not finish
        {process.draft.message ? `: ${process.draft.message}` : '.'} It can be started again on
        Process maps.
      </Callout>
    );
  }
  return process.ready ? (
    <Callout tone="tip" icon={GitBranch} actions={open}>
      {process.name} has no map yet. {plural(process.confirmed_evidence, 'confirmed quote')} in this
      area can be drawn into one on Process maps; its steps then list here with the quotes behind
      them.
    </Callout>
  ) : (
    <Callout tone="tip" icon={GitBranch}>
      {process.name} can be drafted once this area has confirmed evidence.
    </Callout>
  );
}

export function ProcessStepList({
  process,
  said,
  painPoints,
  selectedKey,
  onSelect,
  onAsk,
  onOpenMap,
}: {
  process: AreaProcess;
  /** The area's confirmed quotes, to say who said a cited quote and where. */
  said: ReadonlyArray<QuotedEvidence>;
  painPoints: ReadonlyArray<PainPoint>;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** Ask about a step nobody has described. */
  onAsk?: (row: StepRow) => void;
  onOpenMap: () => void;
}) {
  const saidById = React.useMemo(() => new Map(said.map((e) => [e.evidence_id, e])), [said]);
  const pains = React.useMemo(() => new Map(painPoints.map((p) => [p.id, p])), [painPoints]);

  if (process.error) {
    return (
      <div className="p-5">
        <ErrorMessage error={process.error} />
      </div>
    );
  }
  if (process.loading) {
    return (
      <p className="flex items-center gap-2 p-5 text-body-sm text-ink-500">
        <Spinner /> Loading the process…
      </p>
    );
  }
  if (!process.process) return null;
  if (!process.map) {
    return (
      <div className="p-5">
        <NotDrafted process={process.process} onOpenMap={onOpenMap} />
      </div>
    );
  }
  if (process.rows.length === 0) {
    return (
      <p className="p-5 text-body-sm text-ink-500">
        The map of {process.process.name} has no steps yet.
      </p>
    );
  }
  return (
    <div role="list" aria-label={`Steps of ${process.process.name}`} className="pb-3">
      {laneRuns(process.rows).map((run, i) => (
        <div role="listitem" key={`${run.lane_key}-${i}`}>
          <p className="flex items-baseline gap-2 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            {run.lane_name || 'No lane'}
            <span className="font-normal normal-case tracking-normal">
              {plural(run.rows.length, 'step')} ·{' '}
              {run.described === 0 ? 'none described' : `${run.described} described`}
            </span>
          </p>
          {run.rows.map((row) => (
            <StepItem
              key={row.key}
              row={row}
              selected={row.key === selectedKey}
              quote={row.quotes[0]}
              evidence={row.quotes[0] ? saidById.get(row.quotes[0].evidence_id) : undefined}
              pains={pains}
              onSelect={() => onSelect(row.key)}
              onAsk={onAsk ? () => onAsk(row) : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ProcessStepDetail({
  row,
  said,
  painPoints,
  onAskFollowUp,
  onSuggest,
  suggesting = false,
  onAddDocument,
  onOpenMap,
  onOpenPainPoint,
}: {
  row: StepRow | null;
  said: ReadonlyArray<QuotedEvidence>;
  painPoints: ReadonlyArray<PainPoint>;
  /**
   * Absent once the area is covered: a follow-up goes to someone who is not
   * a source yet. Given the step's AI question, the form opens with it picked.
   */
  onAskFollowUp?: (aiProposalId?: string) => void;
  /** Ask AI to draft a question for each step nobody has described (T-0286). */
  onSuggest?: () => void;
  /** AI is drafting them now. */
  suggesting?: boolean;
  onAddDocument: () => void;
  onOpenMap: () => void;
  onOpenPainPoint: (id: string) => void;
}) {
  if (!row) {
    return (
      <p className="text-body-sm text-ink-500">Select a step to see what was said about it.</p>
    );
  }
  const saidById = new Map(said.map((e) => [e.evidence_id, e]));
  const pains = new Map(painPoints.map((p) => [p.id, p]));
  const state = stepState(row);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-body font-semibold text-ink-900">
          Step {row.no} · {row.name}
        </h3>
        <StatusChip tone={state.tone} size="sm">
          {state.label}
        </StatusChip>
      </div>
      <p className="mt-1 text-meta text-ink-500">
        {row.lane_name ? `Done by ${row.lane_name}.` : 'No lane is named for this step.'}{' '}
        {row.system ? `In ${row.system}.` : 'No system is named for this step yet.'}
      </p>

      {row.quotes.length ? (
        <>
          <DetailLabel>What was said</DetailLabel>
          <div className="rounded-card border border-ink-100 px-3.5">
            {row.quotes.map((q) => {
              const e = saidById.get(q.evidence_id);
              const number = q.number ? (
                <span className="font-mono text-meta font-medium text-ink-700">{q.number}</span>
              ) : null;
              return e ? (
                <SaidRow key={q.evidence_id} item={e} compact tags={number} />
              ) : (
                <div key={q.evidence_id} className="py-2">
                  <Quote size="sm">{q.text}</Quote>
                  <p className="mt-1.5 flex flex-wrap items-center gap-2 text-meta text-ink-500">
                    Evidence from another area {number}
                  </p>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <Callout tone={row.needs_attention ? 'warn' : 'tip'} className="mt-3">
          {row.described
            ? 'The client validated a version of the map with this step. No quote describes it.'
            : undescribedLine(row)}{' '}
          {row.described
            ? null
            : 'The map draws it dashed until a confirmed quote describes it or the client validates it.'}
        </Callout>
      )}

      {row.described ? null : row.gap_question ? (
        <Callout tone="ai" icon={Zap} className="mt-2.5">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.06em]">
            Suggested question
          </span>
          <span className="mt-1 block font-serif text-[14px] leading-snug text-ink-900">
            {row.gap_question.text}
          </span>
          <span className="mt-1 block text-meta">{row.gap_question.why}</span>
        </Callout>
      ) : onSuggest ? (
        <p className="mt-2.5 flex flex-wrap items-center gap-2 text-meta text-ink-500">
          {suggesting ? (
            <>
              <Spinner /> AI is drafting a question for each step nobody has described…
            </>
          ) : (
            <>
              No question is drafted for this step yet.
              <button type="button" className={linkishClass()} onClick={onSuggest}>
                Suggest a question
              </button>
            </>
          )}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        {!row.described && onAskFollowUp ? (
          <WorkspaceButton
            variant="primary"
            onClick={() => onAskFollowUp(row.gap_question?.ai_proposal_id)}
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" /> Ask someone a follow-up
          </WorkspaceButton>
        ) : null}
        {!row.described ? (
          <WorkspaceButton onClick={onAddDocument}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add evidence from a document
          </WorkspaceButton>
        ) : null}
        <button type="button" className={linkishClass()} onClick={onOpenMap}>
          <GitBranch className="h-3 w-3" aria-hidden="true" />{' '}
          {row.described
            ? 'Link another quote on the map'
            : 'Link a quote or change the step on the map'}
        </button>
      </div>

      {row.pain_points.length ? (
        <>
          <DetailLabel>
            {row.pain_points.length === 1 ? 'Pain point on this step' : 'Pain points on this step'}
          </DetailLabel>
          {row.pain_points.map((p) => {
            const pain = pains.get(p.id);
            return pain ? (
              <RecordRow key={p.id} onClick={() => onOpenPainPoint(p.id)}>
                <span className="flex-none font-mono text-meta font-medium text-ink-700">
                  {p.number}
                </span>
                <span className="min-w-0 flex-1">{pain.text}</span>
                {pain.severity ? (
                  <StatusChip tone={SEVERITY_TONE[pain.severity]} size="sm">
                    {SEVERITY_LABEL[pain.severity]}
                  </StatusChip>
                ) : null}
              </RecordRow>
            ) : (
              <RecordRow key={p.id} muted>
                <span className="font-mono text-meta font-medium">{p.number}</span>
                <span className="flex-1">A pain point of another area</span>
              </RecordRow>
            );
          })}
        </>
      ) : null}

      {row.described && row.quotes.length === 1 ? (
        <p className="mt-4 text-meta text-ink-500">
          One quote describes this step so far. A second source saying the same would corroborate
          it.
        </p>
      ) : null}
    </div>
  );
}
